// RoomListItem — single row in the sidebar room list.
//
// One row does one thing: render a room and report clicks. Unread,
// channel-type, and agent-only treatment are all projections of the
// `RoomSummary` the caller hands in; the item never reads from context
// directly so its visual variants can be exercised in Storybook with
// plain fixtures.
//
// Tokens come from `packages/theme/src/chat.css`. The per-pixel avatar
// dimensions (20×20 DM avatar, 8×8 presence dot, 28×28 workspace
// avatar) are inlined where the brief names an exact value, matching
// the existing Sidebar component — lifting them into tokens would add
// indirection without reuse.

import type { CSSProperties, MouseEvent as ReactMouseEvent, ReactNode } from "react";
import { BellOff, Bot, Hash, User, Video } from "lucide-react";

import { memo, useContext, useState } from "react";

import { StatusGlyph } from "~/components/StatusGlyph";
import { PresenceIndicator } from "~/components/PresenceIndicator";
import { UserProfileCard } from "~/components/UserProfileCard";
import { MatrixContext } from "~/context/MatrixContext";
import {
  useAuthenticatedMedia,
  type UseAuthenticatedMediaOptions,
} from "~/hooks/useAuthenticatedMedia";
import { useUserProfile } from "~/hooks/useUserProfile";
import { useMeetingActive } from "~/hooks/useMeetingPresence";
import { usePresence } from "~/hooks/usePresence";
import { useUserStatus } from "~/hooks/useMyStatus";
import { getInitials } from "~/lib/initials";
import type { PresenceState, RoomSummary } from "~/lib/matrix";
import { ICON_STROKE } from "~/lib/icon";

export interface RoomListItemProps {
  room: RoomSummary;
  active: boolean;
  onActivate: (roomId: string) => void;
  /** Click the "Take over" pill on an agent-only row. The sidebar wires
   *  this to `MatrixContext.setChannelType(room.id, 'mixed')`. */
  onTakeOver?: (room: RoomSummary) => void;
  /** Right-click: opens the row's menu at the pointer. */
  onContextMenu?: (event: ReactMouseEvent<HTMLDivElement>, room: RoomSummary) => void;
  /**
   * Override the DM peer's presence state. Stories pass this
   * explicitly so each variant renders without a Matrix client. In
   * production the component reads presence from `usePresence`.
   */
  presenceOverride?: PresenceState;
  /**
   * Whether the room's notification level is `mute`. Renders a
   * 12×12 muted-bell glyph between the room name and the unread
   * badge. The parent (`RoomList`) reads the level from the Matrix
   * wrapper per room (see #1309).
   */
  muted?: boolean;
  /**
   * Whether the room has a non-empty draft on the main composer
   * (the threaded composer's draft does not decorate the room row).
   * Renders an 8×8 accent dot before the unread badge per the brief.
   */
  hasDraft?: boolean;
}

// Class names used to drive hover-only affordances (take-over pill,
// pressed states) via the stylesheet injected once per module load.
// Keeping the CSS here — co-located with the only component that reads
// it — makes the coupling explicit.
const STYLE_ID = "chat-room-list-item-styles";
const ROW_CLASS = "chat-room-row";
const PRIMARY_CLASS = "chat-room-row__primary";
const TAKE_OVER_CLASS = "chat-room-row__take-over";

function injectStyles(): void {
  if (typeof document === "undefined") return;
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = `
    .${ROW_CLASS}:hover:not([data-active="true"]) {
      background: var(--chat-surface-raised);
    }
    .${TAKE_OVER_CLASS} {
      opacity: 0;
      transition: opacity var(--chat-motion-fast) var(--chat-motion-ease);
    }
    .${ROW_CLASS}:hover .${TAKE_OVER_CLASS},
    .${TAKE_OVER_CLASS}:focus-visible {
      opacity: 1;
    }
  `;
  document.head.appendChild(style);
}
injectStyles();

function RoomListItemImpl({
  room,
  active,
  onActivate,
  onTakeOver,
  onContextMenu,
  presenceOverride,
  muted = false,
  hasDraft = false,
}: RoomListItemProps) {
  const isAgentOnly = room.channelType === "agent-only";
  const isUnread = room.unreadTotal > 0;
  const isDirect = room.isDirect;
  const isMention = room.unreadHighlight > 0;
  const badgeMention = !isDirect && isMention;
  const badgePlain = isDirect && isUnread;
  const showBadge = !muted && (badgeMention || badgePlain);
  const boldName = !muted && !isDirect && isUnread && !isMention;
  const namePrimary = active || showBadge || boldName;
  const [profileAnchor, setProfileAnchor] = useState<HTMLElement | null>(null);
  const dmUserId = room.isDirect && room.directUserIds.length === 1 ? room.directUserIds[0]! : null;
  const matrix = useContext(MatrixContext);
  const dmStatus = useUserStatus(matrix?.client ?? null, dmUserId);
  const meeting = useMeetingActive(matrix?.client ?? null, room.id);

  const height = room.isDirect
    ? "var(--chat-row-comfortable-dm)"
    : "var(--chat-row-comfortable-channel)";

  // Secondary controls (DM profile, agent-only take-over) are siblings of the
  // activation button, never nested inside it — axe flags nested interactives.
  return (
    <div
      data-active={active}
      className={ROW_CLASS}
      onContextMenu={(event) => onContextMenu?.(event, room)}
      style={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        gap: "var(--chat-space-2)",
        height,
        padding: "0 var(--chat-space-3)",
        border: "none",
        borderRadius: "var(--chat-radius-sm)",
        background: active ? "var(--chat-surface-raised)" : "transparent",
      }}
    >
      {room.isDirect ? (
        <RoomLeading
          room={room}
          presenceOverride={presenceOverride}
          onProfileOpen={(anchor) => setProfileAnchor(anchor)}
        />
      ) : null}
      <button
        type="button"
        onClick={() => onActivate(room.id)}
        aria-current={active ? "true" : undefined}
        className={`${PRIMARY_CLASS} chat-button`}
        style={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          alignItems: "center",
          gap: "var(--chat-space-1)",
          height: "100%",
          border: "none",
          borderRadius: "var(--chat-radius-sm)",
          background: "transparent",
          color: namePrimary ? "var(--chat-fg-primary)" : "var(--chat-fg-readable)",
          fontFamily: "var(--chat-font-sans)",
          fontSize: "var(--chat-type-body-md-size)",
          lineHeight: "var(--chat-type-body-md-line)",
          fontWeight: boldName ? 500 : "var(--chat-type-body-md-weight)",
          textAlign: "left",
          cursor: "pointer",
          padding: 0,
        }}
      >
        {!room.isDirect ? (
          <RoomLeading
            room={room}
            presenceOverride={presenceOverride}
            onProfileOpen={(anchor) => setProfileAnchor(anchor)}
          />
        ) : null}
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "var(--chat-space-1)",
            flex: 1,
            minWidth: 0,
          }}
        >
          <span
            data-feedback-redact="true"
            style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          >
            {room.name}
          </span>
          {meeting ? (
            <Video
              aria-label="Meeting in progress"
              role="img"
              size={12}
              strokeWidth={ICON_STROKE}
              style={{ color: "var(--chat-accent)", flexShrink: 0 }}
            />
          ) : null}
          {dmUserId ? (
            <span style={{ position: "relative", width: 14, height: 14, flexShrink: 0 }}>
              <StatusGlyph status={dmStatus} surface="panel" />
            </span>
          ) : null}
        </span>
        {muted ? <MutedBellGlyph /> : null}
        {hasDraft ? <DraftIndicator /> : null}
        {showBadge ? (
          <UnreadBadge
            count={badgeMention ? room.unreadHighlight : room.unreadTotal}
            accent={badgeMention}
          />
        ) : null}
      </button>
      {isAgentOnly && onTakeOver ? (
        <TakeOverPill disabled={!room.canSetChannelType} onClick={() => onTakeOver(room)} />
      ) : null}
      {profileAnchor && dmUserId ? (
        <UserProfileCard
          userId={dmUserId}
          fallbackDisplayName={room.name}
          fallbackAvatarUrl={room.avatarUrl}
          anchor={profileAnchor}
          roomId={room.id}
          onClose={() => setProfileAnchor(null)}
        />
      ) : null}
    </div>
  );
}

/**
 * 8×8 accent dot. Sits before the unread badge so the unread count
 * always reads as the right-most affordance — drafts are a softer
 * signal and shouldn't reorder the row's primary callout.
 *
 * Carries an accessible label so screen-reader users get the same
 * "draft pending" cue sighted users do, and a `data-testid` for the
 * design and e2e suites that assert per-row decoration.
 */
function DraftIndicator() {
  return (
    <span
      role="img"
      aria-label="Draft"
      data-testid="room-row-draft-indicator"
      style={{
        width: 8,
        height: 8,
        borderRadius: "var(--chat-radius-full)",
        background: "var(--chat-fg-muted)",
        flexShrink: 0,
      }}
    />
  );
}

function MutedBellGlyph() {
  return (
    <BellOff
      aria-label="Muted"
      size={12}
      strokeWidth={ICON_STROKE}
      style={{ color: "var(--chat-fg-muted)", flexShrink: 0 }}
    />
  );
}

/* ─── leading visuals ────────────────────────────────────────────── */

function RoomLeading({
  room,
  presenceOverride,
  onProfileOpen,
}: {
  room: RoomSummary;
  presenceOverride?: PresenceState;
  onProfileOpen: (anchor: HTMLElement) => void;
}): ReactNode {
  if (room.isDirect) {
    return (
      <DmAvatar
        label={room.name}
        avatarUrl={room.avatarUrl}
        peerUserId={room.directUserIds.length === 1 ? room.directUserIds[0]! : null}
        presenceOverride={presenceOverride}
        onProfileOpen={onProfileOpen}
      />
    );
  }
  const Icon = glyphIconFor(room.channelType);
  return (
    <Icon
      aria-hidden
      size={14}
      strokeWidth={ICON_STROKE}
      style={{ color: "var(--chat-fg-secondary)", flexShrink: 0 }}
    />
  );
}

function glyphIconFor(
  channelType: RoomSummary["channelType"],
): typeof Hash | typeof User | typeof Bot {
  if (channelType === "human-only") return User;
  if (channelType === "agent-only") return Bot;
  // `mixed` and the "not set yet" fallback both render the plain `#` —
  // the brief keeps mixed glyph-less.
  return Hash;
}

const AVATAR_MEDIA_OPTS: UseAuthenticatedMediaOptions = {
  reuseAvatar: true,
  thumbnail: { w: 40, h: 40, method: "crop" },
};

const DM_AVATAR_BASE: CSSProperties = {
  position: "relative",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 20,
  height: 20,
  borderRadius: "var(--chat-radius-full)",
  flexShrink: 0,
  fontFamily: "var(--chat-font-sans)",
  fontSize: 10,
  fontWeight: 400,
  color: "var(--chat-fg-secondary)",
  background: "var(--chat-surface-raised)",
};

function DmAvatar({
  label,
  avatarUrl,
  peerUserId,
  presenceOverride,
  onProfileOpen,
}: {
  label: string;
  avatarUrl: string | null;
  peerUserId: string | null;
  presenceOverride?: PresenceState;
  onProfileOpen: (anchor: HTMLElement) => void;
}): ReactNode {
  if (!peerUserId) {
    return <DmAvatarStatic label={label} avatarUrl={avatarUrl} />;
  }
  return (
    <DmAvatarWithPeer
      label={label}
      avatarUrl={avatarUrl}
      peerUserId={peerUserId}
      presenceOverride={presenceOverride}
      onProfileOpen={onProfileOpen}
    />
  );
}

function DmAvatarStatic({
  label,
  avatarUrl,
}: {
  label: string;
  avatarUrl: string | null;
}): ReactNode {
  const initials = getInitials(label);
  const matrix = useContext(MatrixContext);
  const client = matrix?.client ?? null;
  const avatarMxc = avatarUrl && avatarUrl.startsWith("mxc://") ? avatarUrl : null;
  const { src: resolvedHref } = useAuthenticatedMedia(client, avatarMxc);
  return (
    <span
      style={{
        ...DM_AVATAR_BASE,
        backgroundImage: resolvedHref ? `url(${JSON.stringify(resolvedHref)})` : undefined,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      {resolvedHref ? null : initials || "–"}
    </span>
  );
}

function DmAvatarWithPeer({
  label,
  avatarUrl,
  peerUserId,
  presenceOverride,
  onProfileOpen,
}: {
  label: string;
  avatarUrl: string | null;
  peerUserId: string;
  presenceOverride?: PresenceState;
  onProfileOpen: (anchor: HTMLElement) => void;
}): ReactNode {
  const initials = getInitials(label);
  const matrix = useContext(MatrixContext);
  const client = matrix?.client ?? null;
  const roomMxc = avatarUrl && avatarUrl.startsWith("mxc://") ? avatarUrl : null;
  const { avatarUrl: profileAvatarUrl } = useUserProfile(client, peerUserId);
  const profileMxc =
    !roomMxc && profileAvatarUrl && profileAvatarUrl.startsWith("mxc://") ? profileAvatarUrl : null;
  const avatarMxc = roomMxc ?? profileMxc;
  const { src: resolvedHref } = useAuthenticatedMedia(client, avatarMxc, AVATAR_MEDIA_OPTS);
  const dot = <PresenceDotWired peerUserId={peerUserId} presenceOverride={presenceOverride} />;
  const openProfile = (event: ReactMouseEvent<HTMLButtonElement>) =>
    onProfileOpen(event.currentTarget);
  if (resolvedHref) {
    return (
      <button
        type="button"
        aria-label={`Open profile for ${label}`}
        onClick={openProfile}
        className="chat-button"
        style={{
          ...DM_AVATAR_BASE,
          border: "none",
          background: "var(--chat-surface-raised)",
          backgroundImage: `url(${JSON.stringify(resolvedHref)})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          cursor: "pointer",
          padding: 0,
        }}
      >
        {dot}
      </button>
    );
  }
  return (
    <button
      type="button"
      aria-label={`Open profile for ${label}`}
      onClick={openProfile}
      className="chat-button"
      style={{ ...DM_AVATAR_BASE, border: "none", cursor: "pointer", padding: 0 }}
    >
      {initials || "–"}
      {dot}
    </button>
  );
}

/**
 * Subscribe to the DM peer's presence and render the dot. Split out
 * from the avatar so the avatar itself stays a pure projection — only
 * this subcomponent calls hooks. A missing `peerUserId` (defensive
 * guard; a DM always has exactly one peer) renders the offline
 * placeholder rather than branching the avatar's markup.
 */
function PresenceDotWired({
  peerUserId,
  presenceOverride,
}: {
  peerUserId: string | null;
  presenceOverride?: PresenceState;
}): ReactNode {
  // `usePresence` takes a `Client | null` + `userId | null` so
  // detaching is a pure prop change — no conditional hook call. We
  // read the client through the raw context so Storybook (which
  // mounts rows without a MatrixProvider) can still render with
  // `presenceOverride` without throwing. Production callers always
  // live inside the provider so the client is non-null there.
  const matrix = useContext(MatrixContext);
  const client = matrix?.client ?? null;
  const effectiveId = presenceOverride ? null : peerUserId;
  const { state } = usePresence(client, effectiveId);
  const shown = presenceOverride ?? state;
  if (shown === "offline") return null;
  return <PresenceIndicator state={shown} surface="app" ringWidthPx={2} />;
}

/* ─── trailing visuals ────────────────────────────────────────────── */

function UnreadBadge({ count, accent }: { count: number; accent: boolean }) {
  const displayCount = count > 99 ? "99+" : count.toString();
  const label = accent ? `${count} mention${count === 1 ? "" : "s"}` : `${count} unread`;
  return (
    <span
      aria-label={label}
      data-testid="room-row-unread-badge"
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 16,
        height: 16,
        padding: "0 var(--chat-space-1)",
        borderRadius: "var(--chat-radius-full)",
        background: accent ? "var(--chat-mention-bg)" : "transparent",
        color: accent ? "var(--chat-accent)" : "var(--chat-fg-primary)",
        fontFamily: "var(--chat-font-sans)",
        fontSize: "var(--chat-type-body-xs-size)",
        lineHeight: "var(--chat-type-body-xs-line)",
        fontWeight: "var(--chat-type-body-xs-weight)",
        flexShrink: 0,
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {displayCount}
    </span>
  );
}

function TakeOverPill({ disabled, onClick }: { disabled: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      className={`${TAKE_OVER_CLASS} chat-button`}
      disabled={disabled}
      onClick={() => onClick()}
      style={{
        height: 20,
        padding: "0 var(--chat-space-1)",
        border: "none",
        borderRadius: "var(--chat-radius-sm)",
        background: "transparent",
        color: "var(--chat-fg-secondary)",
        fontFamily: "var(--chat-font-sans)",
        fontSize: "var(--chat-type-body-xs-size)",
        lineHeight: "var(--chat-type-body-xs-line)",
        fontWeight: 400,
        cursor: disabled ? "not-allowed" : "pointer",
        flexShrink: 0,
      }}
    >
      Take over
    </button>
  );
}

export const RoomListItem = memo(RoomListItemImpl);
